import React, { useState, useEffect } from 'react';
import {
  CreateDesForm,
  RenderDesProj,
  EditDesForm,
  ProjectSelector,
  PreviewDesContent,
} from '../components';
import { useDispatch, useSelector } from 'react-redux';
import {
  deleteAllDesProjects,
  getDesProjectBody,
  getDesProjects,
} from '../features/projects/desprojectSlice';
import {
  Button,
  Modal,
  Typography,
  Space,
  Tabs,
  Card,
  Divider,
  message,
  Spin,
} from 'antd';
import {
  BgColorsOutlined,
  DeleteOutlined,
  ReloadOutlined,
  ExclamationCircleOutlined,
  EditOutlined,
  EyeOutlined,
  PlusOutlined,
} from '@ant-design/icons';

const { Title, Text } = Typography;
const { TabPane } = Tabs;
const { confirm } = Modal;

const Design = () => {
  const [activeTab, setActiveTab] = useState('create');
  const [bodyContent, setBodyContent] = useState('');
  const [selectedProject, setSelectedProject] = useState(null);
  const dispatch = useDispatch();
  const { desprojects, loading, bodyLoading, bodyError } = useSelector(
    (state) => state.desprojects
  );

  const handlePurgeProjects = () => {
    confirm({
      title: 'Delete All Design Projects',
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p>
            Are you sure you want to delete{' '}
            <strong>all {desprojects.length} design projects</strong>?
          </p>
          <p className='text-red-600 font-medium'>
            This action cannot be undone and will permanently remove:
          </p>
          <ul className='mt-2 ml-4 text-red-600'>
            <li>• All project data and case studies</li>
            <li>• All uploaded design images</li>
            <li>• All project links and metadata</li>
            <li>• All design documentation and process notes</li>
          </ul>
        </div>
      ),
      okText: 'Yes, Delete All',
      okType: 'danger',
      cancelText: 'Cancel',
      width: 500,
      onOk() {
        dispatch(deleteAllDesProjects());
      },
    });
  };

  const [refreshing, setRefreshing] = useState(false);

  // Replace your handleRefresh function with this enhanced version
  const handleRefresh = async () => {
    setRefreshing(true);

    try {
      // Refresh the development projects data
      await dispatch(getDesProjects()).unwrap();

      // If there's a selected project, refresh its body content as well
      if (selectedProject) {
        try {
          const result = await dispatch(
            getDesProjectBody(selectedProject._id)
          ).unwrap();
          setBodyContent(result.body || '');
        } catch (error) {
          console.log('Error refreshing project body:', error);
        }
      }
    } catch (error) {
      // Show error message if refresh fails
      message.error('Failed to refresh projects');
      console.error('Refresh error:', error);
    } finally {
      // Stop the spinning animation
      setRefreshing(false);
    }
  };

  const handleAddDetailedContent = () => {
    if (desprojects.length === 0) {
      Modal.info({
        title: 'No Projects Available',
        content:
          'Please create a design project first before adding a case study.',
        onOk: () => setActiveTab('create'),
      });
      return;
    }
    setActiveTab('editor');
  };

  const handleProjectSelect = async (project) => {
    setSelectedProject(project);

    // If the project already has body content, use it
    if (project.body && project.body.trim()) {
      setBodyContent(project.body);
    } else {
      // Try to fetch the body content from the server
      try {
        const result = await dispatch(getDesProjectBody(project._id)).unwrap();
        setBodyContent(result.body || '');
      } catch (error) {
        console.log('No body content found or error fetching:', error);
        setBodyContent('');
      }
    }

    setActiveTab('editor');
  };

  // Helper function to check if content is truly empty
  const isContentEmpty = (content) => {
    if (!content) return true;

    // Remove HTML tags and check if any meaningful content remains
    const textContent = content
      .replace(/<[^>]*>/g, '') // Remove all HTML tags
      .replace(/&nbsp;/g, ' ') // Replace &nbsp; with space
      .trim(); // Remove whitespace

    return textContent === '';
  };

  const handleSaveContent = () => {
    setActiveTab('projects');
    setSelectedProject(null);
    setBodyContent('');
  };

  const handleCancelEdit = () => {
    setActiveTab('projects');
    setSelectedProject(null);
    setBodyContent('');
  };

  // Count projects with content for badge
  const projectsWithContent = desprojects.filter(
    (project) =>
      project.body && project.body.trim() && !isContentEmpty(project.body)
  ).length;

  return (
    <div className='max-w-7xl mx-auto p-6 bg-gray-50 min-h-screen'>
      {/* Header */}
      <div className='mb-8'>
        <div className='flex items-center justify-between'>
          <div className='flex items-center space-x-3'>
            <BgColorsOutlined className='text-2xl text-purple-600' />
            <div>
              <Title level={2} className='m-0'>
                Design Projects
              </Title>
              <Text className='text-gray-600'>
                Create and manage your design portfolio and case studies
              </Text>
            </div>
          </div>
          <Space>
            <Button
              icon={<EditOutlined />}
              onClick={handleAddDetailedContent}
              type='primary'
              ghost
            >
              Add Case Study
            </Button>
            {desprojects.length > 0 && (
              <Button
                icon={<DeleteOutlined />}
                onClick={handlePurgeProjects}
                loading={loading}
                danger
                type='primary'
              >
                Purge All ({desprojects.length})
              </Button>
            )}
            <Button
              icon={<ReloadOutlined spin={refreshing} />} // Icon spins when refreshing
              onClick={handleRefresh}
              loading={refreshing} // Button shows loading state
              disabled={refreshing} // Prevents multiple clicks
            >
              Refresh
            </Button>
          </Space>
        </div>
      </div>

      {/* Main Content */}
      <Card className='shadow-soft border border-gray-200 rounded-xl bg-white'>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          size='large'
          className='min-h-[600px]'
        >
          <TabPane
            tab={
              <Space>
                <BgColorsOutlined />
                Create Project
              </Space>
            }
            key='create'
          >
            <div className='p-4'>
              <CreateDesForm />
            </div>
          </TabPane>

          <TabPane
            tab={
              <Space>
                🎨 My Projects
                {desprojects.length > 0 && (
                  <span className='bg-purple-100 text-purple-800 px-2 py-1 rounded-full text-xs font-medium'>
                    {desprojects.length}
                  </span>
                )}
              </Space>
            }
            key='projects'
          >
            <div className='p-4'>
              {desprojects.length > 0 ? (
                <>
                  <div className='mb-6 p-4 bg-purple-50 border border-purple-200 rounded-lg'>
                    <div className='flex items-center justify-between'>
                      <div>
                        <Text className='text-purple-800 font-medium block'>
                          🎨 Portfolio Summary
                        </Text>
                        <Text className='text-purple-600 text-sm'>
                          You have {desprojects.length} design project
                          {desprojects.length !== 1 ? 's' : ''} in your
                          portfolio
                        </Text>
                      </div>
                      <div className='text-right'>
                        <div className='text-2xl font-bold text-purple-800'>
                          {desprojects.length}
                        </div>
                        <div className='text-xs text-purple-600 uppercase tracking-wide'>
                          Projects
                        </div>
                      </div>
                    </div>
                  </div>

                  <Divider
                    orientation='left'
                    className='text-gray-600 font-medium'
                  >
                    Your Design Projects
                  </Divider>

                  <RenderDesProj onEditContent={handleProjectSelect} />
                </>
              ) : (
                <div className='text-center py-16'>
                  <BgColorsOutlined className='text-6xl text-gray-400 mb-4' />
                  <Title level={3} className='text-gray-500 mb-2'>
                    No Design Projects Yet
                  </Title>
                  <Text className='text-gray-400 text-base mb-6 block'>
                    Start building your portfolio by creating your first design
                    project
                  </Text>
                  <Button
                    type='primary'
                    size='large'
                    onClick={() => setActiveTab('create')}
                    className='bg-purple-600 hover:bg-purple-700'
                  >
                    Create Your First Project
                  </Button>
                </div>
              )}
            </div>
          </TabPane>

          <TabPane
            tab={
              <Space>
                <EyeOutlined />
                Preview Case Studies
                {projectsWithContent > 0 && (
                  <span className='bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium'>
                    {projectsWithContent}
                  </span>
                )}
              </Space>
            }
            key='preview'
            disabled={projectsWithContent === 0}
          >
            <div className='p-4'>
              <PreviewDesContent onEditContent={handleProjectSelect} />
            </div>
          </TabPane>

          <TabPane
            tab={
              <Space>
                <EditOutlined />
                {selectedProject
                  ? `${selectedProject.name} Case Study`
                  : 'Select Project'}
              </Space>
            }
            key='editor'
            disabled={desprojects.length === 0}
          >
            <div className='p-6'>
              {selectedProject ? (
                <EditDesForm
                  selectedProject={selectedProject}
                  onCancel={handleCancelEdit}
                  onSave={handleSaveContent}
                  bodyContent={bodyContent}
                  setBodyContent={setBodyContent}
                />
              ) : (
                <>
                  {desprojects.length > 0 ? (
                    <ProjectSelector
                      projects={desprojects}
                      onProjectSelect={handleProjectSelect}
                      type='des'
                      isContentEmpty={isContentEmpty}
                    />
                  ) : (
                    <Card className='mb-6'>
                      <div className='text-center py-12'>
                        <BgColorsOutlined className='text-6xl text-gray-400 mb-4' />
                        <Title level={4} className='text-gray-500 mb-2'>
                          No Projects Available
                        </Title>
                        <Text className='text-gray-400 mb-6 block'>
                          Create your first design project to add a case study
                        </Text>
                        <Button
                          type='primary'
                          size='large'
                          onClick={() => setActiveTab('create')}
                          className='bg-purple-600 hover:bg-purple-700'
                        >
                          Create Your First Project
                        </Button>
                      </div>
                    </Card>
                  )}
                </>
              )}
            </div>
          </TabPane>
        </Tabs>
      </Card>

      {/* Quick Stats Footer */}
      {desprojects.length > 0 && (
        <div className='mt-6 grid grid-cols-1 md:grid-cols-4 gap-4'>
          <Card size='small' className='text-center'>
            <div className='text-2xl font-bold text-green-600'>
              {desprojects.filter((p) => p.status === true).length}
            </div>
            <div className='text-sm text-gray-600'>Completed</div>
          </Card>
          <Card size='small' className='text-center'>
            <div className='text-2xl font-bold text-orange-600'>
              {desprojects.filter((p) => p.status === false).length}
            </div>
            <div className='text-sm text-gray-600'>In Progress</div>
          </Card>
          <Card size='small' className='text-center'>
            <div className='text-2xl font-bold text-purple-600'>
              {new Set(desprojects.flatMap((p) => p.stacks || [])).size}
            </div>
            <div className='text-sm text-gray-600'>Design Tools Used</div>
          </Card>
          <Card size='small' className='text-center'>
            <div className='text-2xl font-bold text-blue-600'>
              {projectsWithContent}
            </div>
            <div className='text-sm text-gray-600'>With Case Studies</div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default Design;
